import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Play, RefreshCw, Clock, Save } from "lucide-react";
import { authFetch } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import ModuleHeader from "@/components/ModuleHeader";

interface ModuleSchedule {
  id: number;
  modulo: string;
  label: string;
  cron_expression: string;
  descricao: string | null;
  ativo: boolean;
  ultima_execucao: string | null;
  proxima_execucao: string | null;
  criado_em: string;
}

function fmtDate(d: string | null) {
  if (!d) return "Nunca";
  return new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function cronToHuman(expr: string): string {
  const parts = expr.split(" ");
  if (parts.length < 5) return expr;
  const [min, hour, dom, mon, dow] = parts;

  if (dom === "*" && mon === "*" && dow === "*") {
    return `Diario as ${hour}:${min.padStart(2, "0")}`;
  }
  if (dow === "0" && dom === "*" && mon === "*") {
    return `Semanal (dom) as ${hour}:${min.padStart(2, "0")}`;
  }
  if (dom === "1" && mon === "*" && dow === "*") {
    return `Mensal (dia 1) as ${hour}:${min.padStart(2, "0")}`;
  }
  if (mon.includes(",")) {
    return `Trimestral (dia ${dom}) as ${hour}:${min.padStart(2, "0")}`;
  }
  return expr;
}

function ScheduleCard({ schedule }: { schedule: ModuleSchedule }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [editCron, setEditCron] = useState(schedule.cron_expression);
  const [dirty, setDirty] = useState(false);

  const updateMut = useMutation({
    mutationFn: (body: Partial<ModuleSchedule>) =>
      authFetch(`/api/scheduler/modules/${schedule.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      toast({ title: "Agendamento atualizado" });
      qc.invalidateQueries({ queryKey: ["/api/scheduler/modules"] });
      setDirty(false);
    },
    onError: (e: any) =>
      toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const runMut = useMutation({
    mutationFn: () =>
      authFetch(`/api/scheduler/modules/${schedule.id}/run`, { method: "POST" }),
    onSuccess: (r: any) => {
      toast({ title: r?.message || "Execucao iniciada" });
      qc.invalidateQueries({ queryKey: ["/api/scheduler/modules"] });
    },
    onError: (e: any) =>
      toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  return (
    <Card
      className={schedule.ativo ? "border-green-800/30" : "border-muted opacity-60"}
      data-testid={`schedule-card-${schedule.modulo}`}
    >
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{schedule.label}</span>
            <Badge
              variant={schedule.ativo ? "default" : "secondary"}
              className="text-xs"
            >
              {schedule.ativo ? "Ativo" : "Inativo"}
            </Badge>
          </div>
          <Switch
            checked={schedule.ativo}
            onCheckedChange={(v) => updateMut.mutate({ ativo: v })}
            data-testid={`switch-ativo-${schedule.modulo}`}
          />
        </div>

        {schedule.descricao && (
          <p className="text-xs text-muted-foreground">{schedule.descricao}</p>
        )}

        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Input
              value={editCron}
              onChange={(e) => {
                setEditCron(e.target.value);
                setDirty(e.target.value !== schedule.cron_expression);
              }}
              className="font-mono text-xs h-8"
              placeholder="Cron expression"
              data-testid={`input-cron-${schedule.modulo}`}
            />
          </div>
          {dirty && (
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => updateMut.mutate({ cron_expression: editCron })}
              disabled={updateMut.isPending}
              data-testid={`button-save-cron-${schedule.modulo}`}
            >
              <Save className="h-3 w-3 mr-1" />
              Salvar
            </Button>
          )}
        </div>

        <div className="text-xs text-muted-foreground">
          {cronToHuman(schedule.cron_expression)}
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-muted-foreground">Ultima execucao</span>
            <p className="font-medium">{fmtDate(schedule.ultima_execucao)}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Proxima execucao</span>
            <p className="font-medium">
              {schedule.ativo ? fmtDate(schedule.proxima_execucao) : "—"}
            </p>
          </div>
        </div>

        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          data-testid={`button-run-${schedule.modulo}`}
        >
          <Play className="h-3 w-3 mr-1.5" />
          Executar agora
        </Button>
      </CardContent>
    </Card>
  );
}

export default function RotinasPage() {
  const schedulesQ = useQuery<ModuleSchedule[]>({
    queryKey: ["/api/scheduler/modules"],
    queryFn: () => authFetch("/api/scheduler/modules"),
    refetchInterval: 30000,
  });

  const schedules = schedulesQ.data || [];
  const activeCount = schedules.filter((s) => s.ativo).length;

  return (
    <div className="space-y-6" data-testid="page-rotinas">
      <ModuleHeader
        icon={Calendar}
        iconColor="text-indigo-500"
        title="Rotinas & Agendamentos"
        description="Gerencie os agendamentos automaticos de importacao e sincronizacao de todos os modulos"
        recordCount={activeCount}
        recordLabel="rotinas ativas"
        onRefresh={() => schedulesQ.refetch()}
        refreshing={schedulesQ.isFetching}
        isLoading={schedulesQ.isLoading}
      />

      {schedulesQ.isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array(6)
            .fill(0)
            .map((_, i) => (
              <Skeleton key={i} className="h-48 w-full rounded-lg" />
            ))}
        </div>
      ) : schedules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Clock className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Nenhum agendamento configurado. Os agendamentos serao criados automaticamente ao iniciar o servidor.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {schedules.map((s) => (
            <ScheduleCard key={s.id} schedule={s} />
          ))}
        </div>
      )}
    </div>
  );
}
